import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, FileSpreadsheet, ChevronDown, ChevronUp, TrendingUp, AlertTriangle, BarChart3, Percent, Scale, BookOpen, Brain, Zap, TrendingDown, DollarSign, Database, ShieldAlert, SlidersHorizontal, GitCompareArrows } from 'lucide-react';

export default function SpreadsheetBenefits() {
  const navigate = useNavigate();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/spreadsheet-comparison')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-spreadsheet"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Spreadsheet Growth Comparison
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <FileSpreadsheet className="h-5 w-5 sm:h-7 sm:w-7" />
              Spreadsheet Growth Comparison
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            <WhatsNewSection />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of Spreadsheet Upload</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-2 sm:mb-3">
                    Why Use Spreadsheet Growth Comparison?
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Uploading your daily production spreadsheet transforms raw farm data into structured biological insight. 
                    Our advanced Missing Fish Intelligence engine analyzes your data using multiple detection methods to identify 
                    stock discrepancies before they impact your bottom line.
                  </p>
                </section>

                {/* Missing Fish Intelligence Section */}
                <section className="bg-purple-50 border border-purple-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <Brain className="h-4 w-4" />
                    Missing Fish Intelligence
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3">
                    Our unified detection engine uses multiple methods to estimate missing fish with SSOT (Single Source of Truth) confidence scoring — every section of the report displays the same resolved detection strength:
                  </p>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <TrendingDown className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span><strong>FCR-Based Detection:</strong> Compares reported FCR against biological model FCR to identify when fewer fish are consuming the feed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Zap className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Sample Jump Analysis:</strong> Detects sudden upward weight jumps that indicate fish loss events. Uses gap-weighted blending of trajectory and model baseline for conservative estimates, with full ranges shown</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <BarChart3 className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Growth-Fit Analysis:</strong> Uses R² curve fitting to determine the fish count that best matches observed growth patterns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ShieldAlert className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Conservative Planning Estimate:</strong> When methods disagree, the lower estimate is the recommended planning figure — reducing overstatement risk. Estimation quality (Good/Moderate/Low) is shown based on data density</span>
                    </li>
                  </ul>
                </section>

                {/* Core Feature List */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-3">Core Analysis Features:</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Compare actual growth against SGR biological model (extended to 800g)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Identify suppressed growth or compensatory growth patterns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Detect potential missing fish with economic impact estimation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Track daily and accumulated mortality trends</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Percent className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Measure feeding consistency and detect hidden overfeeding risk</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Scale className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Evaluate feeding discipline with per-sample deviation analysis</span>
                    </li>
                  </ul>
                </section>

                {/* Advanced Analytics Section */}
                <section className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Advanced Analytics
                  </h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Data Quality Indicator:</strong> Scores your data completeness (0-100) across sample frequency, feed continuity, mortality reporting, and temperature coverage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Growth Intelligence Summary:</strong> Consolidated view of growth direction, confidence level, and missing fish assessment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Mortality Reality Check:</strong> Flags when reported mortality seems unusually low relative to biomass trends</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Incorrect Sample Detection:</strong> Automatically identifies likely erroneous sample weights with severity scoring</span>
                    </li>
                  </ul>
                </section>

                {/* Feed Variance Comparison */}
                <section className="bg-teal-50 border border-teal-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                    <GitCompareArrows className="h-4 w-4" />
                    Feed Variance Comparison
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3">
                    When Missing Fish Intelligence detects stock discrepancies, the Feed Variance Comparison recalculates biological feed requirements using the corrected fish count. Stock accuracy and feeding impact are treated as separate signals:
                  </p>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      <span><strong>Corrected RFI (Relative Feed Index):</strong> Actual Feed / Corrected Recommended Feed. The corrected requirement is based on the adjusted fish count — not the reported count — ensuring the feeding comparison is biologically valid</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      <span><strong>Missing fish does not automatically mean feed waste:</strong> If Corrected RFI is 100% or below, feeding remains within the biological requirement of the corrected biomass — no waste has occurred, and economic impact is zero</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      <span><strong>Economic Impact (EUR):</strong> Only calculated when Corrected RFI exceeds 100%. Excess Feed = Actual Feed minus Corrected Recommended Feed, multiplied by your feed cost per kilogram. This ensures only true overfeeding is costed — not theoretical losses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      <span><strong>Suspected Date of Fish Loss:</strong> Interactive timeline to model when stock loss likely occurred, producing a hybrid comparison before and after the loss date</span>
                    </li>
                  </ul>
                </section>

                {/* Customizable Report Sections */}
                <section className="bg-sky-50 border border-sky-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-sky-800 mb-3 flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    Customizable Report Sections
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3">
                    Control exactly which sections appear in your report with 15 individually toggleable sections across 4 groups. Administrators can hide specific sections globally — hidden sections are automatically removed from the selector for non-admin users:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 text-xs sm:text-sm text-gray-700">
                    <div className="bg-white rounded-lg p-3 border border-sky-100">
                      <p className="font-medium text-sky-700 mb-1">Growth Analysis</p>
                      <p>Growth Chart, Summary Metrics, Feeding & Mortality Metrics, Daily Data Table</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-sky-100">
                      <p className="font-medium text-sky-700 mb-1">Feed & Efficiency</p>
                      <p>Biological FCR, Mortality Chart, Feed Chart, Feed Variance, Feeding Recommendation, Economic Impact</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-sky-100">
                      <p className="font-medium text-sky-700 mb-1">Stock Intelligence</p>
                      <p>Stock Integrity Score, FCR Detection Analysis, Sample Jump Analysis, MFI Report</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-sky-100">
                      <p className="font-medium text-sky-700 mb-1">Quick Presets</p>
                      <p>Growth View, Feed View, Diagnostics View — one-click section configurations</p>
                    </div>
                  </div>
                </section>

                {/* Economic Impact */}
                <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Economic Impact Analysis
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Input your feed price, fish price, and target harvest weight to see the estimated revenue risk from missing fish. 
                    The system calculates potential losses based on detected stock discrepancies, helping you prioritize investigations.
                    Select any FCR multiplier version from any genetic origin for flexible cross-origin benchmarking.
                  </p>
                </section>

                {/* Questions It Answers */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-3">It helps answer:</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Is my growth tracking the model, and if not, why?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>How many fish are likely missing and what's the financial impact?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Is my FCR too good to be true (indicating missing fish)?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>When did the fish loss likely occur (loss window)?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Am I overfeeding fish that are not in the cage?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>What is the feed cost impact of feeding the wrong number of fish?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>How does the reported feed compare to what was needed for the adjusted fish count?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Are any of my sample weights likely incorrect?</span>
                    </li>
                  </ul>
                </section>

                {/* Conclusion */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <p className="text-xs sm:text-sm text-[#0891b2] font-medium leading-relaxed">
                    This module converts operational data into decision-grade intelligence, giving you confidence in your stock numbers 
                    and actionable insights to protect your margins.
                  </p>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component */}
            <HowToUseSection 
              featureName="Spreadsheet Growth Comparison"
              youtubeUrl={HELP_URLS.spreadsheetComparison.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/spreadsheet-comparison')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="start-analysis-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Start Spreadsheet Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
